import { CenterFocusStrong, ZoomIn, ZoomOut } from "@mui/icons-material";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { IDimensions } from "../../hooks/useCreateImageURL";

interface ICropDialogProps {
	open: boolean;
	imageSrc: string;
	imageDimensions: IDimensions;
	initialCoordinates?: [number, number, number, number];
	onClose: () => void;
	onCropComplete: (coordinates: [number, number, number, number]) => void;
}

const CropDialog: FC<ICropDialogProps> = (props) => {
	const { open, imageSrc, imageDimensions, initialCoordinates, onClose, onCropComplete } = props;

	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [isSelecting, setIsSelecting] = useState<boolean>(false);
	const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(null);
	const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
	const [currentSelection, setCurrentSelection] = useState<{
		x: number;
		y: number;
		width: number;
		height: number;
	} | null>(null);

	// Zoom and pan state
	const [zoomLevel, setZoomLevel] = useState<number>(1);
	const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
	const [isPanning, setIsPanning] = useState<boolean>(false);
	const [panStart, setPanStart] = useState<{ x: number; y: number } | null>(null);

	// Image cache to avoid redrawing the same image
	const imageCacheRef = useRef<HTMLImageElement | null>(null);
	const lastRenderParamsRef = useRef<{
		zoomLevel: number;
		panOffset: { x: number; y: number };
		canvasWidth: number;
		canvasHeight: number;
	} | null>(null);

	// Zoom control functions
	const handleZoomIn = useCallback(() => {
		setZoomLevel((prev) => Math.min(prev * 1.2, 5)); // Max zoom 5x
	}, []);

	const handleZoomOut = useCallback(() => {
		setZoomLevel((prev) => Math.max(prev / 1.2, 0.1)); // Min zoom 0.1x
	}, []);

	const handleResetZoom = useCallback(() => {
		setZoomLevel(1);
		setPanOffset({ x: 0, y: 0 });
	}, []);

	// Function to render the image with caching
	const renderImage = useCallback(() => {
		if (!canvasRef.current || !imageSrc) {
			return;
		}

		const canvas = canvasRef.current;
		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		// Always use the actual canvas element dimensions, not the state
		const width = canvas.offsetWidth || canvas.clientWidth;
		const height = canvas.offsetHeight || canvas.clientHeight;

		if (width <= 0 || height <= 0) {
			return;
		}

		// Set canvas internal dimensions if they don't match
		if (canvas.width !== width || canvas.height !== height) {
			canvas.width = width;
			canvas.height = height;
		}

		// Check if we need to redraw based on parameters
		const currentParams = {
			zoomLevel,
			panOffset: { ...panOffset },
			canvasWidth: width,
			canvasHeight: height
		};

		const lastParams = lastRenderParamsRef.current;
		const needsRedraw =
			!lastParams ||
			lastParams.zoomLevel !== currentParams.zoomLevel ||
			lastParams.panOffset.x !== currentParams.panOffset.x ||
			lastParams.panOffset.y !== currentParams.panOffset.y ||
			lastParams.canvasWidth !== currentParams.canvasWidth ||
			lastParams.canvasHeight !== currentParams.canvasHeight;

		if (!needsRedraw && imageCacheRef.current) {
			// Just redraw the cached image
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			ctx.save();
			ctx.translate(panOffset.x, panOffset.y);
			ctx.scale(zoomLevel, zoomLevel);
			ctx.drawImage(imageCacheRef.current, 0, 0, canvas.width, canvas.height);
			ctx.restore();

			// Draw initial selection if it exists
			if (initialCoordinates && !currentSelection) {
				const [x, y, width, height] = initialCoordinates;
				const scaleX = canvas.width / imageDimensions.width;
				const scaleY = canvas.height / imageDimensions.height;
				const screenX = x * scaleX * zoomLevel + panOffset.x;
				const screenY = y * scaleY * zoomLevel + panOffset.y;
				const screenWidth = width * scaleX * zoomLevel;
				const screenHeight = height * scaleY * zoomLevel;

				ctx.strokeStyle = "#4caf50";
				ctx.lineWidth = 2;
				ctx.strokeRect(screenX, screenY, screenWidth, screenHeight);
			}

			// Draw current selection if it exists
			if (currentSelection) {
				ctx.strokeStyle = "#4caf50";
				ctx.lineWidth = 2;
				ctx.strokeRect(currentSelection.x, currentSelection.y, currentSelection.width, currentSelection.height);
			}

			return;
		}

		// Need to redraw - load image and cache it
		const img = new Image();
		img.onload = () => {
			// Cache the image
			imageCacheRef.current = img;
			lastRenderParamsRef.current = currentParams;

			// Clear the canvas
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			// Save the current context state
			ctx.save();

			// Apply zoom and pan transformations
			ctx.translate(panOffset.x, panOffset.y);
			ctx.scale(zoomLevel, zoomLevel);

			// Draw the image
			ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

			// Restore the context state
			ctx.restore();

			// Draw initial selection if it exists (transform to screen coordinates)
			if (initialCoordinates && !currentSelection) {
				const [x, y, width, height] = initialCoordinates;
				// Scale coordinates to match canvas size
				const scaleX = canvas.width / imageDimensions.width;
				const scaleY = canvas.height / imageDimensions.height;

				// Transform from image coordinates to screen coordinates
				const screenX = x * scaleX * zoomLevel + panOffset.x;
				const screenY = y * scaleY * zoomLevel + panOffset.y;
				const screenWidth = width * scaleX * zoomLevel;
				const screenHeight = height * scaleY * zoomLevel;

				ctx.strokeStyle = "#4caf50";
				ctx.lineWidth = 2;
				ctx.strokeRect(screenX, screenY, screenWidth, screenHeight);
			}

			// Draw current selection if it exists
			if (currentSelection) {
				ctx.strokeStyle = "#4caf50";
				ctx.lineWidth = 2;
				ctx.strokeRect(currentSelection.x, currentSelection.y, currentSelection.width, currentSelection.height);
			}
		};
		img.onerror = (error) => {
			console.error("Image failed to load:", error);
		};
		img.src = imageSrc;
	}, [imageSrc, zoomLevel, panOffset, initialCoordinates, currentSelection, imageDimensions]);

	// Effect to render image when dialog opens or image changes
	useEffect(() => {
		if (open && imageSrc) {
			// Use a small timeout to ensure the canvas is fully rendered in the DOM
			const timer = setTimeout(() => {
				renderImage();
			}, 50);
			return () => clearTimeout(timer);
		}
	}, [open, imageSrc, renderImage]);

	// Effect to render when zoom, pan, or selection changes
	useEffect(() => {
		if (open && imageSrc) {
			const animationFrame = requestAnimationFrame(() => {
				renderImage();
			});
			return () => cancelAnimationFrame(animationFrame);
		}
	}, [zoomLevel, panOffset, currentSelection, open, imageSrc, renderImage]);

	// Effect to reset zoom and pan when dialog closes
	useEffect(() => {
		if (!open) {
			// Reset zoom and pan to original values
			setZoomLevel(1);
			setPanOffset({ x: 0, y: 0 });
			setCurrentSelection(null);
			setIsSelecting(false);
			setIsPanning(false);
			setPanStart(null);
			setStartPoint(null);

			// Clear image cache
			imageCacheRef.current = null;
			lastRenderParamsRef.current = null;
		}
	}, [open]);

	const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
		if (!canvasRef.current) return;

		const rect = canvasRef.current.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		// Check if middle mouse button or Ctrl+click for panning
		if (e.button === 1 || (e.button === 0 && e.ctrlKey)) {
			setIsPanning(true);
			setPanStart({ x, y });
			return;
		}

		setIsSelecting(true);
		setStartPoint({ x, y });
		setCurrentSelection({ x, y, width: 0, height: 0 });
	}, []);

	const handleMouseMove = useCallback(
		(e: React.MouseEvent<HTMLCanvasElement>) => {
			if (!canvasRef.current) return;

			const rect = canvasRef.current.getBoundingClientRect();
			const currentX = e.clientX - rect.left;
			const currentY = e.clientY - rect.top;

			// Handle panning
			if (isPanning && panStart) {
				const deltaX = currentX - panStart.x;
				const deltaY = currentY - panStart.y;

				// Update pan offset
				setPanOffset((prev) => ({
					x: prev.x + deltaX,
					y: prev.y + deltaY
				}));

				// Update selection coordinates to move with the image
				if (currentSelection) {
					setCurrentSelection((prev) =>
						prev
							? {
									x: prev.x + deltaX,
									y: prev.y + deltaY,
									width: prev.width,
									height: prev.height
							  }
							: null
					);
				}

				// Update start point for selection if we're in the middle of selecting
				if (startPoint) {
					setStartPoint((prev) =>
						prev
							? {
									x: prev.x + deltaX,
									y: prev.y + deltaY
							  }
							: null
					);
				}

				setPanStart({ x: currentX, y: currentY });
				return;
			}

			// Handle selection
			if (isSelecting && startPoint) {
				const width = currentX - startPoint.x;
				const height = currentY - startPoint.y;

				// Selection coordinates are in screen space (canvas coordinates)
				// They will be transformed to image coordinates in handleApply
				const newSelection = {
					x: width < 0 ? currentX : startPoint.x,
					y: height < 0 ? currentY : startPoint.y,
					width: Math.abs(width),
					height: Math.abs(height)
				};

				setCurrentSelection(newSelection);
			}
		},
		[isSelecting, startPoint, isPanning, panStart, currentSelection]
	);

	const handleMouseUp = useCallback(() => {
		setIsSelecting(false);
		setIsPanning(false);
		setPanStart(null);
	}, []);

	const handleApply = useCallback(() => {
		if (currentSelection && canvasRef.current) {
			// Convert screen coordinates to image coordinates
			// Since selection coordinates are now adjusted during panning,
			// we need to account for the current pan offset
			const imageX = (currentSelection.x - panOffset.x) / zoomLevel;
			const imageY = (currentSelection.y - panOffset.y) / zoomLevel;
			const imageWidth = currentSelection.width / zoomLevel;
			const imageHeight = currentSelection.height / zoomLevel;

			// Scale coordinates back to original image dimensions
			const scaleX = imageDimensions.width / canvasRef.current.width;
			const scaleY = imageDimensions.height / canvasRef.current.height;

			// Final coordinates in original image space
			const finalX = imageX * scaleX;
			const finalY = imageY * scaleY;
			const finalWidth = imageWidth * scaleX;
			const finalHeight = imageHeight * scaleY;

			// Ensure coordinates are within image bounds
			const clampedX = Math.max(0, Math.min(finalX, imageDimensions.width));
			const clampedY = Math.max(0, Math.min(finalY, imageDimensions.height));
			const clampedWidth = Math.max(0, Math.min(finalWidth, imageDimensions.width - clampedX));
			const clampedHeight = Math.max(0, Math.min(finalHeight, imageDimensions.height - clampedY));

			onCropComplete([clampedX, clampedY, clampedWidth, clampedHeight]);
		}
		onClose();
	}, [currentSelection, onClose, onCropComplete, imageDimensions, zoomLevel, panOffset]);

	// Keep canvas dimensions state for the canvas element (used in JSX)
	useEffect(() => {
		if (canvasRef.current && canvasRef.current.offsetWidth) {
			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				width: canvasRef.current?.offsetWidth ?? 0
			}));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [canvasRef.current]);

	useEffect(() => {
		if (canvasDimensions.width && imageDimensions) {
			const canvasHeight = (canvasDimensions.width * imageDimensions.height) / imageDimensions.width;

			setCanvasDimensions((currentDimensions) => ({
				...currentDimensions,
				height: canvasHeight
			}));
		}
	}, [canvasDimensions.width, imageDimensions]);

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6">Adjust Crop</Typography>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<IconButton onClick={handleZoomOut} size="small" title="Zoom Out">
							<ZoomOut />
						</IconButton>
						<Typography variant="body2" sx={{ minWidth: "60px", textAlign: "center" }}>
							{Math.round(zoomLevel * 100)}%
						</Typography>
						<IconButton onClick={handleZoomIn} size="small" title="Zoom In">
							<ZoomIn />
						</IconButton>
						<IconButton onClick={handleResetZoom} size="small" title="Reset Zoom">
							<CenterFocusStrong />
						</IconButton>
					</Box>
				</Box>
			</DialogTitle>
			<DialogContent>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: "center" }}>
					Click and drag to select crop area. Hold Ctrl and drag to pan when zoomed in.
				</Typography>
				<Box
					sx={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						p: 2,
						width: "100%"
					}}
				>
					<Box
						sx={{
							width: "100%",
							position: "relative",
							aspectRatio: `${imageDimensions.width}/${imageDimensions.height}`
						}}
					>
						<canvas
							ref={canvasRef}
							width={canvasDimensions.width}
							height={canvasDimensions.height}
							style={{
								border: "1px solid #ddd",
								cursor: isPanning ? "grabbing" : "crosshair",
								width: "100%",
								height: "100%",
								backgroundColor: "#f5f5f5"
							}}
							onMouseDown={handleMouseDown}
							onMouseMove={handleMouseMove}
							onMouseUp={handleMouseUp}
							onContextMenu={(e) => e.preventDefault()} // Prevent right-click context menu
						/>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose}>Cancel</Button>
				<Button onClick={handleApply} variant="contained" color="primary">
					Apply
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CropDialog;
